<script setup>
// Page 2 — the problem, in the three moments a board or manager recognises. This is
// the page the hero's promise is measured against: before Pandig explains anything
// about itself, the visitor has to see their own month described.
//
// Scroll-scrubbed over a taller sticky section (the same shape as the other tall
// pages), with one difference from the reveals elsewhere: the heading and the intro
// are never hidden, and the three rows fade up from DIM rather than from nothing.
// A section's top edge is where its scrub sits at zero, and a fast fling can drop you
// anywhere in the range — the argument has to be readable wherever you land.
// What the scrub buys is the sequence: the three moments arrive one at a time, in
// the order they happen in a real month, instead of landing as a wall of text.
//
// One ink frame with hairline rows, not three icon cards: the moments are entries in
// a register, the same one-frame-hairlines-inside language as the app's data table,
// and the frame carries the page's single hard shadow (One Hero Rule).
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { gsap, ScrollTrigger, useMotion, reducedMotion, EASE, SCRUB, DIM } from "@/lib/motion";

const { t } = useI18n();

const cards = [{ id: "card1" }, { id: "card2" }, { id: "card3" }];

const root = ref(null);
const cardEls = ref([]);

let cleanup = () => {};

onMounted(() => {
  cleanup = useMotion(root.value, () => {
    if (reducedMotion) return;

    gsap.set(cardEls.value, { opacity: DIM, y: 18 });

    const tl = gsap.timeline({ paused: true }).to(cardEls.value, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: EASE,
      stagger: 0.35,
    });

    ScrollTrigger.create({
      trigger: root.value,
      start: "top top",
      end: "bottom bottom",
      scrub: SCRUB,
      animation: tl,
    });
  });
});

onUnmounted(() => cleanup());
</script>

<template>
  <!-- Nothing here starts blank, so a fling or a deep link that comes to rest mid-scrub
       still lands on a page that reads. A deliberate jump asks for more than that,
       though — greyed-out cards are legible, not readable, and being handed them is
       what makes a jump feel like it stopped short — so it goes to the far end of the
       scroll range, with the three moments arrived (see scrollElementIntoView). -->
  <section
    id="probleem"
    ref="root"
    class="relative border-t-2 border-foreground bg-card"
    :class="reducedMotion ? 'page-panel flex items-center' : 'h-[125vh] sm:h-[150vh]'"
    data-jump="settled"
    :data-page-label="t('landing.pager.problem')"
  >
    <div :class="reducedMotion ? 'w-full' : 'page-camera flex [align-items:safe_center] overflow-y-auto lg:items-center lg:overflow-hidden'">
      <div class="mx-auto w-full max-w-6xl px-5 py-5 sm:py-14">
        <h2 class="max-w-3xl font-sans text-xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
          {{ t("landing.problem.title") }}
        </h2>
        <!-- The intro carries the four concrete exceptions, so it stays readable on a
             phone rather than being the first thing dropped for height. -->
        <p class="mt-2 max-w-3xl text-xs leading-snug text-muted-foreground sm:mt-4 sm:text-base sm:leading-relaxed">
          {{ t("landing.problem.lead") }}
        </p>

        <ol class="mt-4 border-2 border-foreground bg-background shadow-[var(--shadow-hard)] sm:mt-9">
          <li
            v-for="(card, i) in cards"
            :key="card.id"
            :ref="(el) => (cardEls[i] = el)"
            class="border-t-2 border-foreground/10 p-3 first:border-t-0 sm:grid sm:grid-cols-[16rem_1fr] sm:gap-6 sm:p-6"
          >
            <h3 class="font-sans text-sm font-semibold leading-snug tracking-tight text-foreground sm:text-base">{{ t(`landing.problem.${card.id}_title`) }}</h3>
            <p class="mt-1 text-xs leading-snug text-muted-foreground sm:mt-0 sm:text-sm sm:leading-relaxed">{{ t(`landing.problem.${card.id}_body`) }}</p>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>
